import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../auth/AuthHandler';
import NotificationModal from '../TicketComponents/NotificationModal';
import ConfirmationModal from '../TicketComponents/ConfirmationModal';
import { useServiceTypes, useDefaultStages, useStageCatalog } from '../../hooks/useTicketStages';
import ErrorReworkModal from '../TicketComponents/ErrorReworkModal';
import ModalPortal from '../TicketComponents/ModalPortal';
import { useApiClient } from '../../service/API';
import { getApiErrorMessage } from '../../service/errorHandler';

// NEW: subscribe to store
import { useTicketIds, useTicketById } from '../../state/ticketsStore';

function formatInTZ(dateString, timeZone, withTime) {
  const date = new Date((dateString || '') + 'Z');
  if (isNaN(date)) return '';
  const datePart = date.toLocaleDateString('en-GB', {
    timeZone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  if (!withTime) return datePart;
  const timePart = date.toLocaleTimeString('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${datePart} ${timePart}`;
}

const getSystemTimeZone = () => new Intl.DateTimeFormat().resolvedOptions().timeZone;

function useSystemTZ() {
  return useMemo(() => getSystemTimeZone(), []);
}

function TicketRow({
  id,
  filters,
  tempFilter,
  onCERButton,
  onReverseSort,
  onApplyFilters,
  services,
  allStages,
  userRoles,
}) {
  const apiClient = useApiClient();
  const systemTimeZone = useSystemTZ();
  const ticket = useTicketById(id); // row-level subscription
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalTextTitle, setModalTextTitle] = useState('');
  const [modalLabels, setModalLabels] = useState('');
  const [actionTextType, setActionTextType] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [modalStageList, setModalStageList] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  // early return if not present (filtered out or not seeded yet)
  if (!ticket) return null;

  const startOfDayUTCInTZ = (date, timeZone) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date).reduce((acc, p) => (p.type !== 'literal' ? { ...acc, [p.type]: p.value } : acc), {});
    return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
  };

  const isWithinReworkErrorWindow = (completedAt, timeZone, days = 30) => {
    if (!completedAt) return false;
    const completed = new Date(completedAt);
    if (Number.isNaN(+completed)) return false;
    const msPerDay = 24 * 60 * 60 * 1000;
    const completedMidnightUTC = startOfDayUTCInTZ(completed, timeZone);
    const todayMidnightUTC = startOfDayUTCInTZ(new Date(), timeZone);
    const daysOld = Math.floor((todayMidnightUTC - completedMidnightUTC) / msPerDay);
    return daysOld < days;
  };

  const raiseRework = (t) => {
    if (['ADMIN'].some(role => userRoles.includes(role))) {
      return true;
    }
    if (['ERROR', 'REWORK', 'NEW', 'CANCELLED'].includes(t.status)) return false;
    if (t.status === 'DONE') {
      return isWithinReworkErrorWindow(t.ticketCompletedDate, systemTimeZone, 30);
    }
    return true;
  };

  const handleOpenModal = (type, t) => {
    setTicketId(t.id);
    setActionTextType(type);

    setModalTextTitle(
      type === 'ERROR'
        ? `Enter reason for raising an ERROR\n(TICKET ${t.id}):`
        : `Enter reason for raising a REWORK\n(TICKET ${t.id}):`
    );

    setModalLabels(type);

    // ✅ normalize selectedStageName (may come as JSON string)
    let stageNames = t.selectedStageName;

    // case 1: it's a JSON string
    if (typeof stageNames === 'string') {
      try { stageNames = JSON.parse(stageNames); } catch { stageNames = null; }
    }

    // case 2: it's ["[...json...]"] (array with 1 json string)
    if (Array.isArray(stageNames) && stageNames.length === 1 && typeof stageNames[0] === 'string' && stageNames[0].trim().startsWith('[')) {
      try { stageNames = JSON.parse(stageNames[0]); } catch { /* ignore */ }
    }

    const names = (Array.isArray(stageNames) ? stageNames : allStages)
      .filter(Boolean)
      .filter(n => n !== 'All Tickets' && n !== 'Ticket Closed');

    const idx = names.findIndex(n => n === t.stage);
    const list = idx === -1 ? names : names.slice(0, idx + 1);

    setModalStageList(list);
    setModalOpen(true);
  };

  const handleApiError = (error, defaultMessage) => {
    const message = getApiErrorMessage(error, defaultMessage);
    setShowNotification(true);
    setNotificationMessage(message);
  };

  const handleSubmitReason = async (input, stage) => {
    try {
      const res = await apiClient.patch(`/tickets/update-ticket/error-rework/${ticketId}`, null, {
        params: { status: actionTextType, reason: input, stage }
      });
      if (res.status === 200) {
        onApplyFilters(); // keep your existing auto-refresh-once; store row will be updated via WS or you can fetch single row here
      }
    } catch (error) {
      handleApiError(error, 'Error completing action. Please contact IT support.');
    } finally {
      setModalOpen(false);
      setNotificationMessage(`Ticket ${ticketId} have been updated to ${modalLabels}!`);
      setShowNotification(true);
    }
  };

  const userPart = (s) => (s ? String(s).split('@')[0] : '-');
  
  return (
    <>
      <tr key={ticket.id}>
        <td className={`content ${[1, 2, 3, 4].includes(ticket?.inProgressOrder) ? 'sob-urgent' : ''}`}>
          {ticket.id}
        </td>
        <td className='content'>
          {ticket.sysFreightRef}
        </td>
        <td className={`content ${[1, 2, 3, 4].includes(ticket?.inProgressOrder) && ticket.serviceType?.includes('Import') ? 'sob-urgent' : ''}`}>
          {ticket.serviceType?.includes('Import') && (formatInTZ(ticket.sobatadate ? ticket.sobatadate : ticket.etdetadate, systemTimeZone, false))}
        </td>
        <td className={`content ${[1, 2, 3, 4].includes(ticket?.inProgressOrder) && ticket.serviceType?.includes('Export') ? 'sob-urgent' : ''}`}>
          {(ticket.serviceType?.includes('Export')||ticket.serviceType?.includes('Cross Trade')) && (formatInTZ(ticket.sobatadate ? ticket.sobatadate : ticket.etdetadate, systemTimeZone, false))}
        </td>
        <td className='content'>
          {ticket.serviceType}
        </td>
        <td className={`content ${( [1,2,3,4].includes(ticket?.inProgressOrder) ? 'sob-urgent' : '' )}`}>
          {ticket?.stage ?? ''}
        </td>
        <td className={`content ${ticket?.urgentStatus ? ticket?.urgentStatus : ticket?.status}`}>
          {ticket?.urgentStatus ? ticket?.urgentStatus : ticket?.status}
        </td>
        <td className='content'>
          <div>
            <span className="date">
              {formatInTZ(ticket.createdDate, systemTimeZone, true)}
            </span>
          </div>
          <div>
            <span className="user">
              {userPart(ticket.assignedTo)} / {userPart(ticket.createdBy)}
            </span>
          </div>
        </td>
        <td>
          <div className="d-flex">
            <a href={`/view-ticket/${ticket.id}`} className="btn btn-primary ms-3">View</a>
            {(['FOUSER', 'FOMANAGER', 'BOMANAGER', 'ADMIN'].some(role => userRoles?.includes(role)) && !['DONE','CANCELLED'].includes(ticket.status)) && (
              <button className="btn btn-dark ms-3" onClick={() => setShowCancelConfirmation(true)}>Cancel</button>
            )}
            {((['ADMIN'].some(role => userRoles.includes(role))) ||
              (!['BOUSER'].some(role => userRoles?.includes(role)) &&
              !['ERROR', 'REWORK', 'NEW', 'CANCELLED'].includes(ticket.status) &&
              (ticket.status !== 'DONE' || isWithinReworkErrorWindow(ticket.ticketCompletedDate, systemTimeZone, 30)))) && (
                <button className="btn btn-danger ms-3" onClick={() => handleOpenModal('ERROR', ticket)}>RAISE ERROR</button>
            )}
            {raiseRework(ticket) && (
              <button className="btn btn-warning ms-3" onClick={() => handleOpenModal('REWORK', ticket)}>RAISE REWORK</button>
            )}
          </div>
        </td>
      </tr>

      <ModalPortal>
        <ErrorReworkModal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmitReason}
          title={modalTextTitle}
          labels={modalLabels}
          stageList={modalStageList}
        />

        <NotificationModal
          show={showNotification}
          message={notificationMessage}
          onClose={() => setShowNotification(false)}
        />

        <ConfirmationModal
          show={showCancelConfirmation}
          onClose={() => setShowCancelConfirmation(false)}
          onConfirm={() => {
            setShowCancelConfirmation(false);
            onCERButton('CANCELLED', ticket.id);
          }}
          message="Are you sure you want to CANCELLED this ticket?"
          title="Confirm Cancellation"
        />
      </ModalPortal>
    </>
  );
}

/* -------------------------------------------
   Helper: Header Dropdown (Bootstrap-based)
   Always opens below; uses your custom styles.
-------------------------------------------- */
function HeaderDropdown({ id, label, value, onChange, options, allLabel = 'All' }) {
  const display = value || allLabel;
  const setValue = (val) => {
    // mimic a native event for your onApplyFilters handler
    onChange({ target: { name: id, value: val } });
  };

  return (
    <div className="dropdown custom-dropdown w-100">
      <button
        className="btn btn-outline-secondary dropdown-toggle w-100"
        type="button"
        id={`${id}-dropdown`}
        data-bs-toggle="dropdown"
        data-bs-display="static"  // prevent Popper from flipping
        aria-expanded="false"
      >
        {display}
      </button>
      <ul className="dropdown-menu" aria-labelledby={`${id}-dropdown`}>
        <li>
          <a
            className={`dropdown-item ${value === '' ? 'active' : ''}`}
            onClick={() => setValue('')}
          >
            {allLabel}
          </a>
        </li>
        {options.map((opt, i) => {
          const val = opt?.includes?.('All') ? '' : opt;
          if (!opt) return null;
          return (
            <li key={`${id}-${i}`}>
              <a
                className={`dropdown-item ${value === val ? 'active' : ''}`}
                onClick={() => setValue(val)}
              >
                {opt}
              </a>
            </li>
          );
        })}
      </ul>
      <div className="mt-1 small text-muted">{label}</div>
    </div>
  );
}

function TicketTable({
  loading,
  refreshing,
  filters,
  tempFilter,
  onCERButton,
  onReverseSort,
  onApplyFilters
}) {
  const { userRoles } = useAuth();
  const { services } = useServiceTypes();
  const { stages: defaultStages } = useDefaultStages(tempFilter.service);
  const { catalog } = useStageCatalog();
  const allStages = (tempFilter.service ? defaultStages : catalog).map(s => s.name);

  const ids = useTicketIds();

  const getSortIconClass = (column) =>
    filters.sortBy === column ? (filters.sortDirection === 'ASC' ? '' : 'rotated') : '';

  return (
    <>
      {refreshing && (
        <div className="table-refresh-indicator">Refreshing…</div>
      )}
      <table className="table table-bordered">
        <thead className="table-secondary">
          <tr>
            <th scope="col" style={{ width: '120px' }} className="sortable">
              <div className='label'>
                <input
                  type="text"
                  id="ticketId"
                  name="ticketId"
                  placeholder="Ticket Id"
                  value={tempFilter.ticketId ?? ''}
                  onChange={onApplyFilters}
                  className="form-control"
                  maxLength={13}
                />
                <div onClick={() => onReverseSort('ticketId')}>
                  Ticket Id
                  {filters.sortBy === 'ticketId' && (
                    <div className={`sort-icon ${getSortIconClass('ticketId')}`} role="button" tabIndex="0" aria-label="Reverse sort direction">
                      <svg width="18px" height="18px" viewBox="0 0 48 48" focusable="false" fill="currentColor" className="sort-svg">
                        <path fill="none" d="M0 0h48v48H0V0z"></path>
                        <path d="M8 24l2.83 2.83L22 15.66V40h4V15.66l11.17 11.17L40 24 24 8 8 24z"></path>
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </th>

            <th scope="col" style={{ width: '120px' }} className="sortable">
              <div className='label'>
                <input
                  type="text"
                  id="sysFreightRef"
                  name="sysFreightRef"
                  placeholder="Sysfreight Reference No."
                  value={tempFilter.sysFreightRef ?? ''}
                  onChange={onApplyFilters}
                  className="form-control"
                  maxLength={255}
                />
                <div onClick={() => onReverseSort('sysFreightRef')}>
                  SysFreight No.
                  {filters.sortBy === 'sysFreightRef' && (
                    <div className={`sort-icon ${getSortIconClass('sysFreightRef')}`} role="button" tabIndex="0" aria-label="Reverse sort direction">
                      <svg width="18px" height="18px" viewBox="0 0 48 48" focusable="false" fill="currentColor" className="sort-svg">
                        <path fill="none" d="M0 0h48v48H0V0z"></path>
                        <path d="M8 24l2.83 2.83L22 15.66V40h4V15.66l11.17 11.17L40 24 24 8 8 24z"></path>
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </th>

            <th scope="col" style={{ width: '120px' }} className="normal-col"><div className='label'>ETA Date</div></th>
            <th scope="col" style={{ width: '120px' }} className="normal-col"><div className='label'>ETD Date</div></th>

            {/* Service (Dropdown) */}
            <th scope="col" style={{ width: '120px' }} className="normal-col">
              <HeaderDropdown
                id="service"
                label="Service Type"
                value={tempFilter.service ?? ''}
                onChange={onApplyFilters}
                options={Array.isArray(services) ? services : []}
                allLabel="All Services"
              />
            </th>

            {/* Stage (Dropdown) */}
            <th scope="col" style={{ width: '120px' }} className="normal-col">
              <HeaderDropdown
                id="stage"
                label="Stage"
                value={tempFilter.stage ?? ''}
                onChange={onApplyFilters}
                options={Array.isArray(allStages) ? allStages : []}
                allLabel="All Stages"
              />
            </th>

            {/* Status (Dropdown) */}
            <th scope="col" style={{ width: '120px' }} className="normal-col">
              <HeaderDropdown
                id="status"
                label="Status"
                value={tempFilter.status ?? ''}
                onChange={onApplyFilters}
                options={[
                  'NEW',
                  'URGENT',
                  'IN PROGRESS',
                  'WAITING FOR SI',
                  'COMPLETED',
                  'ERROR',
                  'REWORK',
                  'DONE',
                  'CANCELLED',
                ]}
                allLabel="All"
              />
            </th>

            <th scope="col" style={{ width: '120px' }} className="normal-col">
              <div className='label'>Created [ON - TO / BY]</div>
            </th>

            <th scope="col" style={{ width: '120px' }} className="normal-col">
              <div className='label'>Action</div>
            </th>
          </tr>
        </thead>

        <tbody>
          {ids.length === 0 && !loading ? (
            <tr><td colSpan="9" style={{ height: '500px' }} className="text-center">No tickets available</td></tr>
          ) : (
            ids.map(id => (
              <TicketRow
                key={id}
                id={id}
                filters={filters}
                tempFilter={tempFilter}
                onCERButton={onCERButton}
                onReverseSort={onReverseSort}
                onApplyFilters={onApplyFilters}
                services={services}
                allStages={allStages}
                userRoles={userRoles}
              />
            ))
          )}
        </tbody>
      </table>
    </>
  );
}

export default TicketTable;
